import { useRef } from 'react'

import { Box, Button, Section } from '@factorialco/deprecated-design-system'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateEmployeePersonalSituationDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import validateNumberInput from 'modules/core/lib/validateNumberInput'
import app from 'modules/core/store/models/app'

import PersonalSituationForm from 'modules/employees/lib/forms/Personal/PersonalSituation'

const COUNTRIES_WITHOUT_PERSONAL_SITUATION = ['gb', 'us']

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
}

export const PersonalSituation = observer(({ employee, readonly }: Props) => {
  const [updatePersonalSituationData] = useMutation(
    EmployeeBlocksUpdateEmployeePersonalSituationDocument,
    {
      extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
      update: (cache, result) => {
        const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
        if (updatedEmployee) {
          cache.writeFragment({
            id: cache.identify(employee),
            fragment: EmployeeBlocksEmployeeFragmentDoc,
            fragmentName: 'EmployeeBlocksEmployee',
            data: {
              ...employee,
              disabilityPercentageCents: updatedEmployee.disabilityPercentageCents,
            },
          })
        }
      },
    }
  )
  const form = useRef(
    new PersonalSituationForm({
      disability_percentage_cents: employee.disabilityPercentageCents,
    })
  )
  const personalForm = form.current

  const trackEvent = () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(personalForm),
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const onSubmit = async () => {
    trackEvent()

    const disabilityPercentageCents =
      parseInt(personalForm.get('disability_percentage_cents').value) * 100
    const result = await updatePersonalSituationData({
      variables: {
        employeeId: employee.id,
        disabilityPercentageCents: disabilityPercentageCents,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'profile.employee.personal_situation.update_personal_situation_data_error'
          ),
        })
      }
    })

    if (handled.success) {
      personalForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.personal_situation.update_personal_situation_data'),
      })
    }

    return handled
  }

  return (
    <FormComponent>
      <FormRow>
        <Text
          label={i18n.t('employee_form.personal_situation.form.disability_percentage')}
          field={personalForm.get('disability_percentage_cents')}
          readonly={readonly}
          validation={validateNumberInput}
        />
      </FormRow>
      {personalForm.isDirty && (
        <FormRow footer>
          <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </FormComponent>
  )
})

const PersonalSituationWrapper = (props: Props) => {
  const employeeCountry = props.employee.legalEntity?.country

  if (COUNTRIES_WITHOUT_PERSONAL_SITUATION.includes(employeeCountry || '')) {
    return null
  }

  return (
    <Section
      description={i18n.t('employee_form.personal_situation.description')}
      illustration="love-hands"
      title={i18n.t('employee_form.personal_situation.title')}
    >
      <Box maxWidth="s360" alignSelf="end" width="s360">
        <Box
          padding="s24"
          background="grey200"
          borderRadius={{
            all: 'abs004',
          }}
        >
          <PersonalSituation {...props} />
        </Box>
      </Box>
    </Section>
  )
}

export default observer(PersonalSituationWrapper)
